/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    List, ListItem, ListItemText, Stack, Typography, Divider, CircularProgress,
    TextField, InputAdornment, MenuItem, Collapse, Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getUploadsRelativePath } from '../../utils/resolveDocumentUrl';
import { DEFAULT_FOLDERS, buildFolderTree, flattenFoldersForSelect } from '../../utils/documentFolders';

/**
 * Document Previewer Sub-Component
 */
const DocumentPreviewer = ({ filepath }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    const cleanPath = getUploadsRelativePath(filepath);
    const fullUrl = cleanPath.startsWith('http') ? cleanPath : `/api/${cleanPath}`;
    const fileExtension = cleanPath.split('.').pop().toLowerCase();

    useEffect(() => {
        if (fileExtension === 'pdf' && filepath) {
            setLoading(true);
            axios.get(fullUrl, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setBlobUrl(url);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Blob fetch failed:", err);
                    setLoading(false);
                });
        }
        return () => {
            if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        };
    }, [filepath, fileExtension, fullUrl]);

    if (!filepath) return null;

    if (fileExtension === 'pdf') {
        return (
            <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#fff' }}>
                {loading ? (
                    <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading PDF Securely...</Typography>
                    </Stack>
                ) : (
                    <iframe
                        src={blobUrl || fullUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="PDF Preview"
                    />
                )}
            </Box>
        );
    }

    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fff', p: 1 }}>
                <img src={fullUrl} alt="preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
            <Typography>Preview not available for .{fileExtension}</Typography>
        </Box>
    );
};

// 4. Document Linking / Folder Structure: renders one folder node (and its files/subfolders)
// inside the picker, mirroring the Documents tab's own folder tree exactly.
const PickerFolderNode = ({ node, level, documents, openFolders, onToggle, activeDocPath, onDocumentSelect, normalize }) => {
    const filesInFolder = documents.filter((doc) => (doc.folder || '') === node.path);
    const isOpen = !!openFolders[node.path];
    const hasContent = filesInFolder.length > 0 || node.children.length > 0;

    return (
        <React.Fragment>
            <ListItem
                button
                onClick={() => onToggle(node.path)}
                sx={{ pl: 1.5 + level * 1.5, py: 0.4 }}
            >
                <Typography sx={{ flexGrow: 1, fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {isOpen ? '📂' : '📁'} {node.name}
                    <Chip label={filesInFolder.length} size="small" sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.6 } }} />
                </Typography>
                {hasContent && (isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
            </ListItem>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {filesInFolder.map((doc) => (
                    <ListItem
                        button
                        key={doc.id}
                        selected={normalize(activeDocPath) === normalize(doc.filepath)}
                        onClick={() => onDocumentSelect(doc.filepath)}
                        sx={{ pl: 3.5 + level * 1.5, py: 0.3 }}
                    >
                        <ListItemText
                            primary={`📄 ${doc.fileName}`}
                            secondary={`Uploaded: ${doc.uploadDate}`}
                            primaryTypographyProps={{ variant: 'body2', noWrap: true, title: doc.fileName }}
                        />
                    </ListItem>
                ))}
                {node.children.map((child) => (
                    <PickerFolderNode
                        key={child.path}
                        node={child}
                        level={level + 1}
                        documents={documents}
                        openFolders={openFolders}
                        onToggle={onToggle}
                        activeDocPath={activeDocPath}
                        onDocumentSelect={onDocumentSelect}
                        normalize={normalize}
                    />
                ))}
            </Collapse>
        </React.Fragment>
    );
};

const DocumentPicker = ({ open, onClose, matterId, user, onDocumentSelect, folder, activeDocPath, onDocumentDelete }) => {
    const [documents, setDocuments] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [search, setSearch] = useState('');
    const [openFolders, setOpenFolders] = useState({});
    const [uploadFolder, setUploadFolder] = useState(folder || DEFAULT_FOLDERS[0]);
    const fileInputRef = useRef(null);

    const normalize = (path) => path ? path.replace(/\\/g, '/').toLowerCase() : "";

    const fetchDocs = async () => {
        if (!matterId) return;
        try {
            const res = await axios.get(`/api/cashbook/available-documents/${matterId}`);
            setDocuments(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setDocuments([]);
        }
    };

    useEffect(() => {
        if (open) {
            fetchDocs();
            setUploadFolder(folder || DEFAULT_FOLDERS[0]);
            setSearch('');
        }
    }, [open, matterId]);

    // Auto-expand the folder the caller suggested (e.g. "L&D Account" from the L&D tab), and
    // the folder that already contains the currently-linked document, so the user isn't
    // dropped into a fully-collapsed tree every time they open the picker.
    useEffect(() => {
        if (!open) return;
        const toOpen = {};
        const openChain = (path) => {
            if (!path) return;
            const parts = path.split('/');
            let running = '';
            parts.forEach((part) => {
                running = running ? `${running}/${part}` : part;
                toOpen[running] = true;
            });
        };
        if (folder) openChain(folder);
        if (activeDocPath) {
            const activeDoc = documents.find((d) => normalize(d.filepath) === normalize(activeDocPath));
            if (activeDoc?.folder) openChain(activeDoc.folder);
        }
        if (Object.keys(toOpen).length > 0) {
            setOpenFolders((prev) => ({ ...prev, ...toOpen }));
        }
    }, [open, folder, activeDocPath, documents]);

    const allFolders = useMemo(() => {
        const fromDocs = documents.map((d) => d.folder).filter(Boolean);
        return [...new Set([...DEFAULT_FOLDERS, ...fromDocs])];
    }, [documents]);

    const folderTree = useMemo(() => buildFolderTree(allFolders), [allFolders]);
    const folderSelectOptions = useMemo(() => flattenFoldersForSelect(allFolders), [allFolders]);

    const filteredDocuments = useMemo(() => {
        if (!search.trim()) return null; // null = "not searching", render the tree instead
        const term = search.toLowerCase();
        return documents.filter((d) => (d.fileName || '').toLowerCase().includes(term));
    }, [documents, search]);

    const handleToggleFolder = (path) => setOpenFolders((prev) => ({ ...prev, [path]: !prev[path] }));

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = null; // allow re-selecting the same file later
        if (!file || !matterId) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('documents', file, file.name);
        formData.append('folder', uploadFolder || folder || '');
        try {
            const response = await axios.post(`/api/legal-matter/${matterId}/uploads`, formData, {
                headers: {
                    'x-user-email': user?.emailAddress || user?.email || '',
                    'x-user-role': user?.role || '',
                },
            });
            const newDoc = response.data?.files?.[0];
            await fetchDocs();
            // Auto-link: whatever section opened this picker gets the newly uploaded document
            // linked immediately, per item 4's "select, save, auto-link" flow.
            if (newDoc?.filepath && onDocumentSelect) onDocumentSelect(newDoc.filepath);
            setOpenFolders((prev) => ({ ...prev, [uploadFolder]: true }));
        } catch (error) {
            console.error("Upload failed", error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteDocument = async () => {
        if (!activeDocPath || !matterId) return;
        if (window.confirm("Are you sure you want to delete this document? This action cannot be undone.")) {
            setIsDeleting(true);
            try {
                // Extract filename from the full path
                const filename = activeDocPath.split('/').pop();
                await axios.delete(`/api/legal-matter/${matterId}/documents/${filename}`);

                // Notify parent component of deletion
                if (onDocumentDelete) onDocumentDelete(activeDocPath);

            } catch (error) {
                console.error("Delete failed", error);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="h6" sx={{ mr: 'auto' }}>Select Supporting Document</Typography>
                    <TextField
                        select
                        size="small"
                        label="Upload to folder"
                        value={uploadFolder}
                        onChange={(e) => setUploadFolder(e.target.value)}
                        sx={{ minWidth: 220 }}
                    >
                        {folderSelectOptions.map((opt) => (
                            <MenuItem key={opt.path} value={opt.path}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || !matterId}
                    >
                        {isUploading ? 'Uploading...' : 'Upload New'}
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0, height: '600px', display: 'flex' }}>
                <Box sx={{ width: '35%', borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column', bgcolor: '#f9f9f9' }}>
                    <Box sx={{ p: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search documents…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Box>
                    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        <List dense disablePadding>
                            {filteredDocuments === null ? (
                                // Not searching: show the same folder structure as the Documents tab,
                                // instead of every document in the matter as one flat list.
                                folderTree.map((node) => (
                                    <PickerFolderNode
                                        key={node.path}
                                        node={node}
                                        level={0}
                                        documents={documents}
                                        openFolders={openFolders}
                                        onToggle={handleToggleFolder}
                                        activeDocPath={activeDocPath}
                                        onDocumentSelect={onDocumentSelect}
                                        normalize={normalize}
                                    />
                                ))
                            ) : (
                                filteredDocuments.map((doc) => (
                                    <ListItem button key={doc.id} selected={normalize(activeDocPath) === normalize(doc.filepath)} onClick={() => onDocumentSelect(doc.filepath)}>
                                        <ListItemText
                                            primary={`📄 ${doc.fileName}`}
                                            secondary={`${doc.folder ? doc.folder + ' • ' : ''}Uploaded: ${doc.uploadDate}`}
                                        />
                                    </ListItem>
                                ))
                            )}
                            {documents.length === 0 && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No documents uploaded to this matter yet. Choose a folder above and click "Upload New" to add one.
                                    </Typography>
                                </Box>
                            )}
                            {filteredDocuments !== null && filteredDocuments.length === 0 && documents.length > 0 && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No documents match "{search}".</Typography>
                                </Box>
                            )}
                        </List>
                    </Box>
                </Box>
                <Box sx={{ width: '65%', height: '100%', bgcolor: '#525659' }}>
                    {activeDocPath ? (
                        <DocumentPreviewer filepath={activeDocPath} />
                    ) : (
                        <Stack justifyContent="center" alignItems="center" sx={{ height: '100%', color: '#fff' }}>
                            <VisibilityIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                            <Typography>Select a document to preview</Typography>
                        </Stack>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteDocument}
                    disabled={!activeDocPath || isDeleting}
                    sx={{ mr: 'auto' }} // Pushes this button to the left
                >
                    {isDeleting ? 'Deleting...' : 'Delete Selected Document'}
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentPicker;
